import { faCheck, faClose, faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
// import { faSpinnerThird } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getUrlWithSearchParams } from "@helium10/re-core";
import { useMenuStore } from "@helium10/re-features";
import { colorPalette, cssBody400, cssBody600Bold, NewButton } from "@helium10/re-ui-components";
import { memo, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

import { MAX_SINGLE_FILE_SIZE, useUploadFilesStore } from "@/components/UploadFiles/store";
import { useUploadHistoryStore } from "@/components/UploadHistory/store";
import { documentCenterRoute } from "@/core/links";
import { useTranslation } from "@/i18n/useTranslation";
import { getNavigate } from "@/utils/navigateService";

const BULK_UPLOAD_TASK_PREFIX = "document-center-batch-";

// Banner 的层级：要高于 SidebarMenu(z-index: 5) 才能盖住左侧菜单栏，
// 同时必须低于 Header(HeaderWrapper: z-index: 960 / 移动端 971)，否则会压住
// 顶部导航及其 MegaMenu、下拉浮层。900 落在这两者之间。
const BANNER_Z_INDEX = 900;

export const BatchUploadBanner = memo(() => {
  const { t } = useTranslation();
  const tasks = useUploadFilesStore((s) => s.tasks);
  const clearBulkUploadTasks = useUploadFilesStore((s) => s.clearBulkUploadTasks);

  const { totalFiles, completedFiles, isUploading, shouldShow } = useMemo(() => {
    const bulkTasks = Object.entries(tasks).filter(([id]) =>
      id.startsWith(BULK_UPLOAD_TASK_PREFIX),
    );
    let total = 0;
    let completed = 0;
    let uploading = false;
    // uploaded / error 均走完成态绿色 Banner（与产品约定一致）
    let hasTerminalBulkTask = false;

    for (const [, task] of bulkTasks) {
      if (task.status === "uploading") {
        uploading = true;
      }
      if (task.status === "uploaded" || task.status === "error") {
        hasTerminalBulkTask = true;
      }
      // 仅统计实际上传的文件(≤10MB) - 其实就是在排除 excluded 的文件
      // blob 缺失时视为无效
      const validFiles = task.fileList.filter(
        (f) => (f.file?.blob?.size ?? Infinity) <= MAX_SINGLE_FILE_SIZE && f.status !== "excluded",
      );
      // 用户点击上传后 task 才离开 init；分母/分子仅统计已开始上传的任务，避免 init 阶段 total 提前变化
      if (task.status !== "init") {
        total += validFiles.length;
        for (const file of validFiles) {
          // loaded 代表该文件已经上传完成，error 代表该文件上传失败，excluded 代表该文件被排除(没有上传)不在统计范围
          if (file.status === "loaded" || file.status === "error") {
            completed += 1;
          }
        }
      }
    }

    return {
      totalFiles: total,
      completedFiles: completed,
      isUploading: uploading,
      // init 仅有文件时不展示；uploading 或 task 终态（uploaded/error）才展示
      shouldShow: uploading || hasTerminalBulkTask,
    };
  }, [tasks]);

  const handleViewDetail = () => {
    const navigate = getNavigate();
    navigate(getUrlWithSearchParams(documentCenterRoute, {}, { hasAccountId: true }));
    useUploadHistoryStore.getState().openDrawer();
    clearBulkUploadTasks();
  };

  const handleClose = () => {
    clearBulkUploadTasks();
  };

  // Banner 固定定位后需要自己算 top。menuStore.headerHeight 由 MenuV2 写入，值是
  // 「Header 高度 + layoutBanner 高度」（见 MenuV2 的 useHeaderHeightController），
  // 减掉 layoutBannerHeight 才是 Header 自身高度；它会随 Header sticky 收起在
  // 64 / 48 之间切换，所以不能写成常量。
  // 这里只订阅一次并在 selector 里算完：返回的是数字，两个字段任意变化都只触发
  // 一次重渲染，比分别订阅两个字段少一次。
  const headerOnlyHeight = useMenuStore((state) =>
    Math.max(state.headerHeight - state.layoutBannerHeight, 0),
  );

  // slotRef: 留在 layoutBanner 插槽里的占位块。两个作用：
  //   1. 撑出 banner 的高度。MainLayout 用 ResizeObserver 量插槽高度写进 menuStore，
  //      侧边栏 padding-top、Layout 的 min-height 都依赖它；portal 之后 banner 不在
  //      文档流里，没有占位块这些偏移就全部失效（内容会被 banner 盖住）。
  //   2. 提供 banner 的水平几何信息。占位块仍在 .ail-left 里，它的 left / width 就是
  //      「页面内容区」的真实范围。AI 聊天面板展开时 .ail-left 会被挤窄，如果 banner
  //      直接用 left: 0; right: 0 铺满视口，就会盖住右侧聊天面板的顶部（banner 的
  //      z-index 900 高于 .ail-right 的 4，且两者在同一个层叠上下文里）。
  // bannerRef: portal 出去的 banner 本体，用来反量高度回填给占位块。
  const slotRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);
  const [slotGeometry, setSlotGeometry] = useState({ left: 0, width: 0 });

  // 必须用 useLayoutEffect：占位块高度要在浏览器绘制前就位，否则首帧会出现
  // 「内容先上跳、下一帧再被推下去」的抖动。
  useLayoutEffect(() => {
    const slot = slotRef.current;
    const banner = bannerRef.current;
    if (!slot || !banner) {
      return;
    }

    const measure = () => {
      const slotRect = slot.getBoundingClientRect();
      // fixed 定位取的是视口坐标，getBoundingClientRect 正好返回视口坐标，直接用。
      setSlotGeometry((prev) =>
        prev.left === slotRect.left && prev.width === slotRect.width
          ? prev // 保持引用不变，避免占位块高度变化时的多余重渲染
          : { left: slotRect.left, width: slotRect.width },
      );
      setBannerHeight(banner.getBoundingClientRect().height);
    };

    measure();
    // 同时观察两个元素：
    //   - slot: 窗口缩放、聊天面板展开/拖拽宽度时，内容区宽度变化 -> banner 要跟着变窄
    //   - banner: 文案换行导致高度变化 -> 占位块要跟着变高
    // 不会死循环：banner 宽度只取决于 slot 宽度，slot 宽度与自身高度无关，
    // 且两个 state 都做了同值 bail-out。
    const observer = new ResizeObserver(measure);
    observer.observe(slot);
    observer.observe(banner);
    return () => observer.disconnect();
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  const bannerNode = (
    <BannerWrapper
      ref={bannerRef}
      $isUploading={isUploading}
      // top / left / width 都是每帧可能变化的像素值，用内联 style 而不是
      // styled-components 的插值：后者每换一个数值就会生成一个新的 class，
      // 拖拽聊天面板宽度时会往 <style> 里灌入上百条规则。
      style={{ top: headerOnlyHeight, left: slotGeometry.left, width: slotGeometry.width }}
    >
      {isUploading ? (
        <UploadingWrapper>
          <TitleWrapper>
            <StyledIcon icon={faSpinnerThird} spin />
            <Title>
              {t("batchUploadBanner.uploading.title", {
                completed: completedFiles,
                total: totalFiles,
              })}
            </Title>
          </TitleWrapper>
          <Subtitle>{t("batchUploadBanner.uploading.subtitle")}</Subtitle>
        </UploadingWrapper>
      ) : (
        <CompletedWrapper>
          <Wrapper>
            <TitleWrapper>
              <StyledIcon icon={faCheck} />
              <Title>
                {t("batchUploadBanner.completed.title", {
                  completed: completedFiles,
                  total: totalFiles,
                })}
              </Title>
            </TitleWrapper>
            <Subtitle>{t("batchUploadBanner.completed.subtitle")}</Subtitle>
          </Wrapper>
          <Actions>
            <NewButton buttonType="primary" size="100" onClick={handleViewDetail}>
              {t("batchUploadBanner.completed.viewDetail")}
            </NewButton>
            <CloseIcon icon={faClose} onClick={handleClose} />
          </Actions>
        </CompletedWrapper>
      )}
    </BannerWrapper>
  );

  return (
    <>
      <Slot ref={slotRef} aria-hidden style={{ height: bannerHeight }} />
      {/* 挂到 body 而不是就地渲染，原因见下方 BannerWrapper 的注释 */}
      {createPortal(bannerNode, document.body)}
    </>
  );
});

// 文档流占位块：不可见，只负责撑高度 + 提供内容区的水平几何信息
const Slot = styled.div`
  width: 100%;
`;

// ⚠️ 这里必须 portal + fixed，单纯调 z-index 解决不了，改动前请先读完：
//
// 需求是 Banner 要盖住左侧菜单栏(SidebarMenu: position: fixed; top: 0;
// height: 100vh; z-index: 5，背景从屏幕顶部铺满，只用 padding-top 把图标推到
// Banner 下方，所以它的灰色背景会压在 Banner 上)。
//
// 但 layoutBanner 插槽渲染在 re-ai-chatbot 的 AILayoutBridgeHost 左侧面板
// (.ail-left) 内，该面板样式为 `position: relative; z-index: var(--ail-z-left)`
// (默认 3)——库里是有意给的显式 z-index，用来形成层叠上下文困住 AI drawer。
// 层叠上下文内部的 z-index 只在内部排序，对外统一按 3 参与比较，所以插槽内部
// 无论写多大的 z-index 都盖不住 body 层级、z-index 5 的侧边栏。
// 把 --ail-z-left 整体抬高同样不行：那会让整个页面内容盖住 hover 展开的侧边菜单
// 和右侧聊天面板，正是库注释里要避免的情况。
//
// 因此只把 Banner 本体 portal 到 document.body（脱离该层叠上下文），用 fixed
// 贴在 Header 正下方，z-index 取 BANNER_Z_INDEX。水平范围由占位块反推，见上文。
const BannerWrapper = styled.div<{ $isUploading: boolean }>`
  position: fixed;
  z-index: ${BANNER_Z_INDEX};
  /* 与 Header sticky 收起、侧边栏的 0.3s 过渡保持一致，避免 Header 变矮时 Banner 跳动 */
  transition:
    top 0.3s,
    width 0.3s;
  padding: 12px 24px 12px 48px;
  background-color: ${({ theme, $isUploading }) =>
    $isUploading ? theme.systemColors.blue50 : colorPalette.green50};
`;
const UploadingWrapper = styled.div``;
const CompletedWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Wrapper = styled.div``;
const TitleWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 18px;
`;
const StyledIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.systemColors.blue600};
`;
const Title = styled.div`
  ${cssBody600Bold}
  font-weight: 800;
`;
const Subtitle = styled.div`
  ${cssBody400}
  font-weight: 400;
  color: ${({ theme }) => theme.systemColors.black};
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sp12};
`;
const CloseIcon = styled(FontAwesomeIcon)`
  cursor: pointer;
`;
